import * as React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

export interface TabItem { id: string; label: React.ReactNode; }

export interface TabsProps {
  items: Array<string | TabItem>;
  value: string;
  onChange?: (id: string) => void;
  variant?: 'underline' | 'pill';
  className?: string;
}

const Strip = styled.div<{ $pill?: boolean }>`
  display: flex;
  gap: 4px;
  ${({ theme, $pill }) =>
    $pill
      ? css`
          gap: 2px;
          background: ${theme.color.bg.muted};
          padding: 3px;
          border-radius: ${theme.radius.base};
          display: inline-flex;
        `
      : css`border-bottom: 1px solid ${theme.color.border.default};`}
`;

const Tab = styled.button<{ $active?: boolean; $pill?: boolean }>`
  background: none;
  border: 0;
  font-family: inherit;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.color.fg.muted};
  cursor: pointer;
  transition: color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.ease},
              border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.ease};
  &:hover { color: ${({ theme }) => theme.color.fg.default}; }

  ${({ theme, $pill, $active }) =>
    $pill
      ? css`
          font-size: ${theme.fontSize.sm};
          padding: 5px 12px;
          border-radius: calc(${theme.radius.base} - 2px);
          ${$active &&
          css`background: ${theme.color.bg.default}; color: ${theme.color.fg.default}; box-shadow: ${theme.shadow.xs};`}
        `
      : css`
          font-size: ${theme.fontSize.base};
          padding: 8px 14px;
          margin-bottom: -1px;
          border-bottom: 2px solid transparent;
          ${$active &&
          css`color: ${theme.color.primary.base}; border-bottom-color: ${theme.color.primary.base}; font-weight: ${theme.fontWeight.semibold};`}
        `}
`;

/** Lumen tabs — controlled, underline (page nav) or pill (segmented control). */
export function Tabs({ items, value, onChange, variant = 'underline', className }: TabsProps) {
  const pill = variant === 'pill';
  return (
    <Strip $pill={pill} className={className} role="tablist">
      {items.map((it) => {
        const id = typeof it === 'string' ? it : it.id;
        const label = typeof it === 'string' ? it : it.label;
        return (
          <Tab key={id} role="tab" aria-selected={value === id} $active={value === id} $pill={pill} onClick={() => onChange?.(id)}>
            {label}
          </Tab>
        );
      })}
    </Strip>
  );
}
